import { useState, type FormEvent } from 'react'
import {
  ModalShell, Field, inputClass, ServerError,
  CancelButton, PrimaryButton, FormStatus
} from '../modal-form'
import { useCreateEventRisk, useUpdateEventRisk } from '../../hooks/useRisks'
import { useUsers } from '../../hooks/useUsers'
import { useDepartments } from '../../hooks/useEvents'
import { bandFor } from '../../lib/risk'
import type { EventListItem, EventRisk, EventRiskBody } from '../../types'

// RiskEditorV2 — spec 12 §3 create/edit modal. Replaces the v1 RiskEditor
// when ?v2=risks is on. v2 additions vs v1: dept select + long-form
// description field. Likelihood × Severity score chips render live with
// band colour.

const CATEGORIES = [
  'Safety',
  'Operational',
  'Financial',
  'Reputational',
  'Environmental'
] as const

const LS_OPTIONS = [1, 2, 3, 4, 5] as const

interface Props {
  event: EventListItem
  risk: EventRisk | null
  onClose: () => void
}

export function RiskEditorV2({ event, risk, onClose }: Props) {
  const isEditing = risk !== null
  const create = useCreateEventRisk(event.id)
  const update = useUpdateEventRisk(event.id)
  const { data: users = [] } = useUsers()
  const { data: depts = [] } = useDepartments()

  const [hazard, setHazard] = useState(risk?.hazard ?? '')
  const [category, setCategory] = useState(risk?.category ?? CATEGORIES[0])
  const [dept, setDept] = useState(risk?.dept ?? '')
  const [description, setDescription] = useState(risk?.description ?? '')
  const [inhL, setInhL] = useState<number>(risk?.inherentLikelihood ?? 3)
  const [inhS, setInhS] = useState<number>(risk?.inherentSeverity ?? 3)
  const [resL, setResL] = useState<number>(risk?.residualLikelihood ?? 2)
  const [resS, setResS] = useState<number>(risk?.residualSeverity ?? 2)
  const [controls, setControls] = useState(risk?.controls ?? '')
  const [ownerUserId, setOwnerUserId] = useState<number | null>(
    risk?.owner?.id ?? null
  )
  const [notes, setNotes] = useState(risk?.notes ?? '')

  const errs: Partial<Record<'hazard' | 'category' | 'controls', string>> = {}
  if (!hazard.trim()) errs.hazard = 'Hazard is required'
  else if (hazard.length > 200) errs.hazard = 'Must be 200 characters or fewer'
  if (!category.trim()) errs.category = 'Category is required'
  if (!controls.trim()) errs.controls = 'Controls are required'

  const isValid = Object.keys(errs).length === 0
  const inhScore = inhL * inhS
  const resScore = resL * resS
  const residualHigherThanInherent = resScore > inhScore

  const mut = isEditing ? update : create

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!isValid || mut.isPending) return
    const body: EventRiskBody = {
      hazard: hazard.trim(),
      category: category.trim(),
      inherentLikelihood: inhL,
      inherentSeverity: inhS,
      controls,
      residualLikelihood: resL,
      residualSeverity: resS,
      ownerUserId,
      notes: notes.trim() || null,
      sortOrder: risk?.sortOrder ?? 9999,
      sourceLibraryId: risk?.sourceLibraryId ?? null,
      dept: dept.trim() || null,
      description: description.trim() || null
    }
    if (isEditing && risk) {
      update.mutate({ riskId: risk.id, body }, { onSuccess: () => onClose() })
    } else {
      create.mutate(body, { onSuccess: () => onClose() })
    }
  }

  return (
    <ModalShell
      title={isEditing ? 'Edit risk' : 'New risk'}
      size="2xl"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <CancelButton onClick={onClose} disabled={mut.isPending} />
          <PrimaryButton disabled={!isValid || mut.isPending}>
            {mut.isPending ? 'Saving…' : isEditing ? 'Save changes' : 'Add risk'}
          </PrimaryButton>
        </>
      }
      body={
        <div className="space-y-4">
          <Field label="Hazard" error={errs.hazard}>
            <input
              type="text"
              value={hazard}
              onChange={(e) => setHazard(e.target.value)}
              autoFocus
              maxLength={200}
              placeholder="e.g. Platform-train interface fall"
              className={inputClass(!!errs.hazard)}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" error={errs.category}>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={inputClass(!!errs.category)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Department" hint="Optional — drives chip colour">
              <select
                value={dept}
                onChange={(e) => setDept(e.target.value)}
                className={inputClass(false)}
              >
                <option value="">— Not set —</option>
                {depts.map((d) => (
                  <option key={d.id} value={d.name}>{d.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Description" hint="Optional context for the risk">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className={`${inputClass(false)} resize-y`}
              placeholder="What's the scenario, where can it happen, what's the impact?"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <ScoreGroup label="Inherent (no controls)" l={inhL} s={inhS} onL={setInhL} onS={setInhS} />
            <ScoreGroup label="Residual (with controls)" l={resL} s={resS} onL={setResL} onS={setResS} />
          </div>

          {residualHigherThanInherent && (
            <FormStatus tone="info">
              Residual ({resScore}) is higher than inherent ({inhScore}) — unusual,
              double-check that controls reduce rather than amplify risk.
            </FormStatus>
          )}

          <Field label="Controls" error={errs.controls}>
            <textarea
              value={controls}
              onChange={(e) => setControls(e.target.value)}
              rows={3}
              className={`${inputClass(!!errs.controls)} resize-y`}
              placeholder="What mitigations are in place? List specific actions."
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner" hint="Person accountable for this risk">
              <select
                value={ownerUserId == null ? '' : String(ownerUserId)}
                onChange={(e) => setOwnerUserId(e.target.value === '' ? null : Number(e.target.value))}
                className={inputClass(false)}
              >
                <option value="">— No owner —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name ?? `(user #${u.id})`}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Notes" hint="Auditor comments">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={1}
                className={`${inputClass(false)} resize-y`}
              />
            </Field>
          </div>

          {mut.isError && (
            <ServerError
              title={`Could not ${isEditing ? 'save' : 'add'} risk`}
              error={mut.error as Error}
            />
          )}
        </div>
      }
    />
  )
}

function ScoreGroup({
  label,
  l,
  s,
  onL,
  onS
}: {
  label: string
  l: number
  s: number
  onL: (v: number) => void
  onS: (v: number) => void
}) {
  const score = l * s
  const band = bandFor(score)
  const bandClass: Record<string, string> = {
    low: 'bg-green/10 text-green border-green/40',
    med: 'bg-brass/10 text-brass border-brass/50',
    high: 'bg-oxblood-soft text-oxblood border-oxblood/40',
    severe: 'bg-oxblood text-parchment border-oxblood'
  }
  return (
    <div>
      <div className="text-xs uppercase tracking-wide font-semibold text-ink-soft mb-2">
        {label}
      </div>
      <div className="flex items-center gap-3 mb-2">
        <span className="font-mono text-sm">L</span>
        <ChipPicker value={l} onChange={onL} />
      </div>
      <div className="flex items-center gap-3 mb-2">
        <span className="font-mono text-sm">S</span>
        <ChipPicker value={s} onChange={onS} />
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold">Score: {score}</span>
        <span
          className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${bandClass[band]}`}
        >
          {band}
        </span>
      </div>
    </div>
  )
}

function ChipPicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex gap-1">
      {LS_OPTIONS.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={
            value === n
              ? 'w-7 h-7 rounded text-xs font-semibold border border-green bg-green text-parchment'
              : 'w-7 h-7 rounded text-xs font-semibold border border-line text-ink-soft hover:bg-parchment'
          }
        >
          {n}
        </button>
      ))}
    </div>
  )
}
